import React, { useEffect, useRef, useState } from 'react';
import { Circle, Line, Group } from 'react-konva';

interface Vec {
  x: number;
  y: number;
}

export interface PointDrawOverlayProps {
  /** Current viewport zoom, so overlay strokes stay a constant screen size. */
  zoom: number;
  /** Committed polyline vertices of the run in progress. */
  vertices: Vec[];
  /**
   * Live next-leg preview paths from the route solver (straights + Curve90s),
   * the landing grid point, and whether the placement is legal.
   */
  preview:
    | {
        paths: number[][];
        endPoint: Vec | null;
        valid: boolean;
      }
    | null;
  /** Grid-snapped cursor (shown as a snapping crosshair). */
  snapPoint: Vec | null;
  /** Nearby logical connection points (junction ports / open rail ends). */
  ports: { x: number; y: number; legal: boolean }[];
  /** Closest (or snapped) connection tip — strongest green highlight. */
  activeTip: Vec | null;
  /** Auto-routed tangent connection toward `activeTip`. */
  route: number[] | null;
  /** Nearest existing-rail centerline point under the cursor (guidance only). */
  centerline?: Vec | null;
  /** Loop-closing affordance shown near the run's start. */
  closeIntent: { start: Vec; active: boolean } | null;
}

const TRACK_RED = '#dc2626';
const EMERALD = '#10b981';
const SKY = '#0ea5e9';
const AMBER = '#f59e0b';
const SLATE = '#94a3b8';

/**
 * Animated visual layer for the Point Draw tool. Lives on its own Konva layer so
 * its per-frame animation only redraws these few nodes, never the element layer.
 * Purely decorative (listening disabled); all committing logic stays in Canvas.
 */
const PointDrawOverlay: React.FC<PointDrawOverlayProps> = ({
  zoom,
  vertices,
  preview,
  snapPoint,
  ports,
  activeTip,
  route,
  centerline,
  closeIntent,
}) => {
  const [now, setNow] = useState(0);
  const raf = useRef<number | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    const loop = (time: number) => {
      if (!alive) return;
      setNow(time);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      alive = false;
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  const z = zoom || 1;
  const period = 1500;
  const phase = (now % period) / period; // 0..1 saw
  const pulse = 0.5 + 0.5 * Math.sin(phase * Math.PI * 2); // 0..1 smooth
  const march = -(phase * 10) / z; // marching-ants dash offset
  const previewColor = preview && !preview.valid ? AMBER : TRACK_RED;

  const isActive = (p: Vec) =>
    !!activeTip &&
    Math.abs(p.x - activeTip.x) < 1e-6 &&
    Math.abs(p.y - activeTip.y) < 1e-6;

  return (
    <Group listening={false}>
      {/* Auto-routed tangent connection (drawn under everything else) */}
      {route && (
        <Line
          points={route}
          stroke={SKY}
          strokeWidth={2.25 / z}
          dash={[7 / z, 4 / z]}
          dashOffset={march}
          lineCap="round"
          lineJoin="round"
          opacity={0.95}
          shadowColor={SKY}
          shadowBlur={8}
          shadowOpacity={0.35 * pulse}
        />
      )}

      {/* Route-solver preview: Curve90 / Curve180 / straights (incl. U-turns) */}
      {preview?.paths?.map((pts, i) => {
        const isCurve = pts.length > 4;
        return (
          <React.Fragment key={`pd-preview-${i}`}>
            {isCurve && (
              <Line
                points={pts}
                stroke={previewColor}
                strokeWidth={4 / z}
                lineCap="round"
                lineJoin="round"
                opacity={0.2 + 0.15 * pulse}
              />
            )}
            <Line
              points={pts}
              stroke={previewColor}
              strokeWidth={2 / z}
              dash={isCurve ? undefined : [6 / z, 4 / z]}
              dashOffset={isCurve ? undefined : march}
              lineCap="round"
              lineJoin="round"
              opacity={0.95}
            />
          </React.Fragment>
        );
      })}

      {/* Committed polyline vertices */}
      {vertices.map((v, i) => (
        <Circle
          key={`pd-v-${i}`}
          x={v.x}
          y={v.y}
          radius={3.5 / z}
          fill="#ffffff"
          stroke={TRACK_RED}
          strokeWidth={1.75 / z}
        />
      ))}

      {/* Start-point loop anchor (subtle breathing dashed ring) */}
      {vertices.length > 0 && (
        <Circle
          x={vertices[0].x}
          y={vertices[0].y}
          radius={(5.5 + 1.5 * pulse) / z}
          stroke={TRACK_RED}
          strokeWidth={1.25 / z}
          opacity={0.5}
          dash={[3 / z, 3 / z]}
        />
      )}

      {/* Nearby endpoint / junction-port highlights (green discovery affordance) */}
      {ports.map((p, i) => {
        if (isActive(p)) {
          return (
            <Group key={`pd-port-${i}`} x={p.x} y={p.y}>
              <Circle
                radius={(8 + 7 * phase) / z}
                stroke={EMERALD}
                strokeWidth={1.75 / z}
                opacity={1 - phase}
              />
              <Circle
                radius={7 / z}
                fill="rgba(16,185,129,0.28)"
                stroke={EMERALD}
                strokeWidth={2.25 / z}
              />
              <Circle radius={2.75 / z} fill={EMERALD} />
            </Group>
          );
        }
        return (
          <Circle
            key={`pd-port-${i}`}
            x={p.x}
            y={p.y}
            radius={5 / z}
            fill={p.legal ? 'rgba(16,185,129,0.18)' : 'rgba(148,163,184,0.12)'}
            stroke={p.legal ? EMERALD : SLATE}
            strokeWidth={1.75 / z}
            dash={p.legal ? undefined : [2 / z, 2 / z]}
            opacity={p.legal ? 0.7 + 0.3 * pulse : 0.55}
          />
        );
      })}

      {/* Mid-span Circle Junction branch target (click inserts hub + starts leg) */}
      {centerline && !isActive(centerline) && (
        <Group x={centerline.x} y={centerline.y}>
          <Circle
            radius={(6 + 2 * pulse) / z}
            stroke={SKY}
            strokeWidth={1.5 / z}
            opacity={0.55 + 0.25 * pulse}
            dash={[3 / z, 2 / z]}
          />
          <Circle radius={2.5 / z} fill={SKY} opacity={0.85} />
        </Group>
      )}

      {/* Snapping crosshair at the grid point the next click lands on */}
      {snapPoint && (
        <Group x={snapPoint.x} y={snapPoint.y}>
          <Line
            points={[-7 / z, 0, 7 / z, 0]}
            stroke={previewColor}
            strokeWidth={1 / z}
            opacity={0.85}
          />
          <Line
            points={[0, -7 / z, 0, 7 / z]}
            stroke={previewColor}
            strokeWidth={1 / z}
            opacity={0.85}
          />
          <Circle
            radius={(4 + 3 * (1 - pulse)) / z}
            stroke={previewColor}
            strokeWidth={1.25 / z}
            opacity={0.35 + 0.4 * pulse}
          />
          <Circle radius={2.75 / z} fill={previewColor} opacity={0.9} />
        </Group>
      )}

      {/* Animated loop-closing ripple at the run's start */}
      {closeIntent?.active && (
        <Group x={closeIntent.start.x} y={closeIntent.start.y}>
          <Circle
            radius={(6 + 18 * phase) / z}
            stroke={EMERALD}
            strokeWidth={2 / z}
            opacity={1 - phase}
          />
          <Circle
            radius={(6 + 18 * ((phase + 0.5) % 1)) / z}
            stroke={EMERALD}
            strokeWidth={2 / z}
            opacity={1 - ((phase + 0.5) % 1)}
          />
          <Circle
            radius={7 / z}
            fill="rgba(16,185,129,0.3)"
            stroke={EMERALD}
            strokeWidth={2 / z}
          />
        </Group>
      )}
    </Group>
  );
};

export default PointDrawOverlay;
